import { COLLECTIONS } from "constants/action-types";
import { Observable, AjaxResponse } from "rxjs";
import {
    mutateCollectionItem,
    FetchSizeRangesAction,
    fetchSizeRangesCompleted,
    MatchSelectedSizesWithSizeRangesAction,
    matchSelectedSizesWithSizeRangesMatched,
    MatchSelectedSizesWithSizeRangesMatchedAction,
    IFetchSizeRangesPayload,
    fetchSizeRangesLoading,
    SetSizeRangeLabelAction,
    lookupAssortmentErrors,
    StartSyncSelectedSizesAction,
    IStartSyncSelectedSizesPayload,
    startSyncSelectedSizes,
    IUpdateCollectionSizePacksToleranceAction,
} from "../../actions/collections";
import {
    Item,
    ITEMS_ID,
    SizeCurveMethod,
    SizeRangesErrors,
    Kinds,
    FieldKeys,
    MANUFACTURED_SIZES_DELIMITER,
    CollectionKeys,
    ValueTypes,
    ValueTypesToMerchantTableMappings,
} from "constants/product-collection";
import { ILookup } from "helpers/merchant/lookup/types";
import {
    transformLookupResponse,
    validateDependencies,
    getDependencies,
    getTransformedLookupValueByType,
    getExecuteSqlResultCache,
} from "helpers/internal-lookups/size-curves-lookup";
import { sizeManufacturedGetter, getSelectTerms, buildFieldToValueTypeMap } from "helpers/product-collection";
import { areAllItemsContainedInList, amphibiousGetIn } from "helpers/objUtils";
import siteConfig from "site-config";
import { formatMessage } from "lib/globalize-util";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import { add as snackbarAdd } from "actions/snackbars";
import { ActionsObservable } from "redux-observable";
import { getFieldValue } from "helpers/collection";
import { get } from "lodash";
import { findOneFieldByKind } from "./helpers/allocation";
import { shouldSyncSelectedSizes, syncSelectedSizes } from "./helpers/sizing";
import { getCollectionItems, getCollectionItemDisplayProperty } from "reducers/selectors/collection";
import { DEFAULT_VALUES_PINNED_ROW } from "components/product-collection/product-collection-views/product-collection-grid-view/detail-list/helpers";
import { mapSetOperation } from "actions/mutate";
import { safeParseInt } from "lib/number";
import { Map } from "immutable";

const generateRequestObservable = (request: any): Observable<any> => {
    const executeSqlResultCache = getExecuteSqlResultCache();
    const requestKey = JSON.stringify(request);
    let requestObservable: Observable<any>;
    if (executeSqlResultCache.has(requestKey)) {
        requestObservable = Observable.from([executeSqlResultCache.get(requestKey)]);
    } else {
        requestObservable = ajax({
            method: "POST",
            body: request,
            url: `${siteConfig.goAPI}/v1/merchanttable/executesql`,
        }).flatMap((response: any) => {
            if (response.status === 200) {
                executeSqlResultCache.set(requestKey, {
                    response: response.response,
                    status: response.status,
                });
            }

            return Observable.from([response]);
        });
    }

    return requestObservable;
};

// https://git.nuorder.com/go/nuorder/-/blob/master/service/merchanttable/merchanttablepb/v1/merchant_table_service.proto#L64
const getSizeRanges = (rawLookupResponse, lookupPlan) => {
    const schemaMap = getSelectTerms(lookupPlan).reduce((acc, { column: { kind, table_column } }) => {
        acc[kind] = table_column;
        return acc;
    }, {});
    const orderField = schemaMap[Kinds.SIZE_ORDER];
    const sizesField = schemaMap[Kinds.SIZES];
    const sizeRangeField = schemaMap[Kinds.SIZE_RANGE];

    return transformLookupResponse(rawLookupResponse).map((resultMap) => {
        const sizeOrder = String(getTransformedLookupValueByType(resultMap[orderField]) ?? "")
            .split(",")
            .map((order) => safeParseInt(order, 0));
        const sizeNames = String(getTransformedLookupValueByType(resultMap[sizesField]) ?? "")
            .split(",")
            .map((size, i) => [size, i])
            .sort((a, b) => sizeOrder[a[1]] - sizeOrder[b[1]])
            .map((size) => String(size[0]));
        return {
            label: String(getTransformedLookupValueByType(resultMap[sizeRangeField]) ?? ""),
            sizes: [...new Set(sizeNames)],
        };
    });
};

const getSizeManufacturedMatched = (sizeRanges, sizeManufactured) => {
    const sizeRangeMatched = sizeRanges.find((sizeRange) =>
        areAllItemsContainedInList(sizeManufactured.split(MANUFACTURED_SIZES_DELIMITER), sizeRange.sizes)
    );
    return sizeRangeMatched;
};

const executeRequest = (
    merchantId: string,
    lookupPlan: ILookup,
    whereFieldValues: (string | number)[],
    valueTypeMap?: Map<string, ValueTypes>
): Observable<AjaxResponse> => {
    const remainingWhereFieldValues = [...whereFieldValues];
    const request = {
        merchant_id: merchantId,
        ...lookupPlan.from,
        statement: {
            select_clause: {
                terms: {
                    terms: getSelectTerms(lookupPlan).map((term) => {
                        let func;
                        if (term.string_aggr) {
                            func = {
                                string_aggr: term.string_aggr,
                            };
                        }
                        return {
                            column: term.column.table_column,
                            alias: "",
                            function: func,
                        };
                    }),
                },
            },
            from_clause: null,
            where_clause: {
                and: {
                    expressions: (lookupPlan.where?.fields || []).map((term) => {
                        const val = remainingWhereFieldValues.shift();
                        const valueType =
                            valueTypeMap?.get(term.column.schema_column) === ValueTypes.NUMBER
                                ? ValueTypesToMerchantTableMappings.NUMBER
                                : "string_value";
                        const value = term.column.schema_column
                            ? (val ?? term.column.enforced_value)
                            : term.column.enforced_value;
                        return {
                            equation: {
                                operand: {
                                    column: term.column.table_column,
                                },
                                compare: {
                                    eq: {
                                        operand: {
                                            value: {
                                                [valueType]: {
                                                    value:
                                                        valueType === ValueTypesToMerchantTableMappings.NUMBER
                                                            ? Number(value)
                                                            : value,
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        };
                    }),
                },
            },
            order: lookupPlan.order,
            limit: {
                val: 1000,
            },
            offset: {
                val: 0,
            },
            group_by: {
                terms: {
                    terms: (lookupPlan.group_by?.columns ?? []).map(({ table_column }) => ({
                        column: table_column,
                        alias: "",
                    })),
                },
            },
        },
    };

    return generateRequestObservable(request).flatMap((response: any) => {
        const { response: body, status } = response;

        if (!body || !body.metadata || !body.rows || status !== 200) {
            return Observable.of(response);
        }

        if (!body.rows.length) {
            if (lookupPlan.where?.fallback) {
                return executeRequest(
                    merchantId,
                    { ...lookupPlan, where: lookupPlan.where.fallback },
                    whereFieldValues,
                    valueTypeMap
                );
            }
        }
        return Observable.of(response);
    });
};

export const matchSelectedSizesWithSizeRangesEpic = (
    action$: ActionsObservable<MatchSelectedSizesWithSizeRangesAction>,
    store
) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.MATCH.START)
        .map(({ payload }) => payload)
        .switchMap(({ collectionId, itemIds, filterByBrand }) => {
            const {
                collection: { gridApi },
                mutate,
            } = store.getState();
            const currentCollection = mutate.snapshots && mutate.snapshots.get(collectionId);
            const merchantId = currentCollection.getIn(["merchant_v1_id", "string_value"], "");
            const itemDisplayProperty = currentCollection
                ? currentCollection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS)
                : undefined;

            if (!itemDisplayProperty) {
                throw new Error("Collection not found");
            }

            const jsItemDisplayProperty = itemDisplayProperty.toJS();
            const fieldToValueTypeMap = buildFieldToValueTypeMap(jsItemDisplayProperty);
            const selectedSizesColumn = findOneFieldByKind(Kinds.SIZES_SELECTED, jsItemDisplayProperty);

            if (!selectedSizesColumn) {
                throw new Error("Selected Size field not found");
            }

            const { lookup_plan } = selectedSizesColumn;

            const fieldsInWhereClauseToMap = getDependencies(lookup_plan)();
            const validateRequiredDependencies = validateDependencies(lookup_plan);

            return (
                Observable.from(itemIds)
                    // retrieve rowNode from gridApi to match against brand
                    .map((id: string): { id: string; rowNode: any } => ({
                        id,
                        rowNode: gridApi.getRowNode(id),
                    }))
                    // filter in brand if exists
                    .filter(
                        ({ rowNode }: { rowNode: any }) =>
                            !filterByBrand || amphibiousGetIn(rowNode.data, Item.FIELDS.BRAND_ID) === filterByBrand
                    )
                    // lookup sizes by merchant
                    .concatMap(({ id, rowNode }: { id: string; rowNode: any }) => {
                        const rowWhereClauseValues = fieldsInWhereClauseToMap.map((field) =>
                            getFieldValue(rowNode.data, field)
                        );
                        const sizeManufactured = sizeManufacturedGetter(null, rowNode);
                        const currentSizeRange = getFieldValue(rowNode.data, FieldKeys.SELECTED_SIZE_RANGE);

                        if (sizeManufactured === "") {
                            store.dispatch(
                                matchSelectedSizesWithSizeRangesMatched({
                                    collectionId,
                                    itemId: id,
                                    sizeRange: null,
                                    selectedSizes: "",
                                    error: SizeRangesErrors.SizeRangesDoNotMatch,
                                })
                            );
                            return [
                                {
                                    [FieldKeys.SIZE_CURVE_METHOD]: SizeRangesErrors.SizeRangesDoNotMatch,
                                },
                            ];
                        }

                        if (currentSizeRange || !!validateRequiredDependencies(rowNode.data.fields)) {
                            return Observable.merge();
                        }

                        return executeRequest(merchantId, lookup_plan, rowWhereClauseValues, fieldToValueTypeMap)
                            .flatMap(({ response, status }) => {
                                if (status !== 200) {
                                    throw new Error(response.message || formatMessage("Unknown fetch error"));
                                }
                                const sizeRanges = getSizeRanges(response, lookup_plan);
                                let error: SizeRangesErrors;
                                const selectedSizeRange = getSizeManufacturedMatched(sizeRanges, sizeManufactured);

                                if (sizeRanges.length === 0) {
                                    error = SizeRangesErrors.SizeRangesNotFound;
                                } else if (!selectedSizeRange) {
                                    error = SizeRangesErrors.SizeRangesDoNotMatch;
                                }
                                store.dispatch(
                                    matchSelectedSizesWithSizeRangesMatched({
                                        collectionId,
                                        itemId: id,
                                        sizeRange: selectedSizeRange,
                                        selectedSizes: selectedSizeRange ? sizeManufactured : "",
                                        error,
                                    })
                                );

                                return [
                                    {
                                        [FieldKeys.SIZE_CURVE_METHOD]: error || SizeCurveMethod.VendorMatched,
                                    },
                                ];
                            })
                            .catch((error) => {
                                if (error.status >= 400) {
                                    return Observable.throw(error);
                                }
                                return Observable.of(
                                    snackbarAdd({ message: formatMessage("Error fetching size ranges") })
                                );
                            });
                    })
                    .toArray()
                    .takeUntil(action$.ofType(COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.MATCH.STOP))
            );
        })
        .concatMap((assortmentsData) => {
            return [
                lookupAssortmentErrors(assortmentsData as any),
                {
                    type: COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.MATCH.COMPLETED,
                },
            ];
        })
        .catch((err) => {
            const errorMessage =
                err.status === 401
                    ? formatMessage("You do not have permission to fetch size ranges")
                    : formatMessage("Error fetching size ranges");

            return Observable.merge(
                Observable.of({
                    type: COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.STOP,
                }),
                Observable.of(snackbarAdd({ message: errorMessage }))
            );
        });
};

export const setSizeRangeLabelEpic = (action$: ActionsObservable<SetSizeRangeLabelAction>, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.SET_SELECTED_SIZE_RANGE)
        .map(({ payload }) => payload)
        .switchMap(({ sizeRangeLabel, itemId }) => {
            const { mutate } = store.getState();
            const collectionItems = mutate.snapshots.get(ITEMS_ID);
            if (!collectionItems) {
                return [];
            }
            const rowIndex = collectionItems.findIndex((collectionItem) => {
                return collectionItem.getIn(Item.FIELDS.ID) === itemId;
            });
            if (rowIndex < 0) {
                return [];
            }

            const row = collectionItems.get(rowIndex);

            const updatedRow = row.setIn(Item.FIELDS.SELECTED_SIZE_RANGE, sizeRangeLabel);

            return [mutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: updatedRow })];
        });
};

export const matchSelectedSizesWithSizeRangesMatchedEpic = (
    action$: ActionsObservable<MatchSelectedSizesWithSizeRangesMatchedAction>,
    store
) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.MATCH.MATCHED)
        .map(({ payload }) => payload)
        .switchMap(({ error, collectionId, itemId, selectedSizes = "", sizeRange }) => {
            const {
                mutate,
                collection: { gridApi },
            } = store.getState();
            const collectionObj = mutate.snapshots.get(collectionId);
            const itemsId = collectionObj?.getIn(["items", "ref", "object_id"]);
            const collectionItems = mutate.snapshots.get(itemsId);
            if (!collectionItems) {
                return [];
            }
            const rowIndex = collectionItems.findIndex((collectionItem) => {
                return collectionItem.getIn(Item.FIELDS.ID) === itemId;
            });
            if (rowIndex < 0) {
                return [];
            }
            const row = collectionItems.get(rowIndex);

            // TODO SIZING make schema based
            const updatedRow = row
                .setIn(Item.FIELDS.SIZE_CURVE_METHOD, error || SizeCurveMethod.VendorMatched)
                .setIn(Item.FIELDS.SIZES_SELECTED, selectedSizes)
                // this one can remain hardcoded since its not set by the scheama
                .setIn(Item.FIELDS.SELECTED_SIZE_RANGE, get(sizeRange, ["label"], ""));

            return [mutateCollectionItem({ itemsId: ITEMS_ID, index: rowIndex, item: updatedRow })];
        });
};

export const fetchSizeRangesEpic = (action$: ActionsObservable<FetchSizeRangesAction>, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.FETCH.START)
        .map(({ payload }) => {
            return payload;
        })
        .switchMap(({ merchantId, itemId, collectionId }: IFetchSizeRangesPayload) => {
            const {
                collection: { gridApi },
                mutate,
            } = store.getState();

            const currentCollection = mutate.snapshots && mutate.snapshots.get(collectionId);
            const itemDisplayProperty = currentCollection
                ? currentCollection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS)
                : undefined;

            if (!itemDisplayProperty) {
                throw new Error("Collection not found");
            }

            const jsItemDisplayProperty = itemDisplayProperty.toJS();
            const fieldToValueTypeMap = buildFieldToValueTypeMap(jsItemDisplayProperty);
            const selectedSizesColumn = findOneFieldByKind(Kinds.SIZES_SELECTED, jsItemDisplayProperty);

            if (!selectedSizesColumn) {
                throw new Error("Selected Size field not found");
            }

            const { lookup_plan } = selectedSizesColumn;
            if (!lookup_plan || itemId === DEFAULT_VALUES_PINNED_ROW) {
                return Observable.of(fetchSizeRangesCompleted({ items: [], error: formatMessage("Please add sizes") }));
            }
            const fieldsInWhereClauseToMap = getDependencies(lookup_plan)();

            const rowNode = gridApi.getRowNode(itemId);
            const rowWhereClauseValues = fieldsInWhereClauseToMap.map((field) => getFieldValue(rowNode.data, field));
            const apiCall$ = executeRequest(merchantId, lookup_plan, rowWhereClauseValues, fieldToValueTypeMap)
                .flatMap(({ response, status }) => {
                    if (status !== 200) {
                        throw new Error(response.message || formatMessage("Unknown fetch error"));
                    }
                    const sizeRanges = getSizeRanges(response, lookup_plan);
                    if (sizeRanges.length === 0) {
                        return [
                            fetchSizeRangesCompleted({
                                error: SizeRangesErrors.SizeRangesNotFound,
                                items: [],
                            }),
                        ];
                    }
                    return [
                        fetchSizeRangesCompleted({
                            items: sizeRanges,
                            error: null,
                        }),
                    ];
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Error fetching size ranges");
                    if (error.status === 401 || error.status === 403) {
                        errorMessage = formatMessage("You do not have permission to fetch size ranges");
                    }
                    return Observable.merge(
                        Observable.of(snackbarAdd({ message: errorMessage })),
                        Observable.of(
                            fetchSizeRangesCompleted({
                                items: [],
                                error: errorMessage,
                            })
                        )
                    );
                });

            return Observable.concat(Observable.of(fetchSizeRangesLoading()), apiCall$);
        });
};

export const syncSelectedSizesOnCollectionLoadEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.FETCH.COMPLETED)
        .map(({ payload }) => payload)
        .switchMap(({ error, collection, isRollUpMode }) => {
            // Short circuit on error on rollup
            if (error || isRollUpMode || !collection) {
                return Observable.empty();
            }

            const itemDisplayProperty = getCollectionItemDisplayProperty(store.getState(), collection.id);
            if (!itemDisplayProperty) {
                return Observable.empty();
            }

            if (!shouldSyncSelectedSizes(itemDisplayProperty)) {
                return Observable.empty();
            }

            return Observable.of(startSyncSelectedSizes(collection.id));
        });
};

export const syncSelectedSizesEpic = (action$: ActionsObservable<StartSyncSelectedSizesAction>, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.SIZING.SYNC_SELECTED_SIZES.START)
        .map(({ payload }) => payload)
        .switchMap(({ collectionId, override }: IStartSyncSelectedSizesPayload) => {
            const itemDisplayProperty = getCollectionItemDisplayProperty(store.getState(), collectionId);
            if (!itemDisplayProperty) {
                throw new Error("Collection item display property not found");
            }

            if (!shouldSyncSelectedSizes(itemDisplayProperty)) {
                return Observable.empty();
            }

            const collectionItems = getCollectionItems(store.getState(), collectionId);
            if (!collectionItems) {
                throw new Error("Collection items not found");
            }

            const mutateActions = [];
            collectionItems.forEach((collectionItem, index) => {
                const syncedItem = syncSelectedSizes(collectionItem, itemDisplayProperty, override);
                if (
                    amphibiousGetIn(collectionItem, Item.FIELDS.SIZES_SELECTED) !==
                    amphibiousGetIn(syncedItem, Item.FIELDS.SIZES_SELECTED)
                ) {
                    mutateActions.push(mutateCollectionItem({ itemsId: ITEMS_ID, index, item: syncedItem }));
                }
            });

            return mutateActions;
        });
};

export const updateCollectionSizeTolerance = (
    action$: ActionsObservable<IUpdateCollectionSizePacksToleranceAction>
) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.SIZE_PACKS.SET_TOLERANCE.START)
        .switchMap(({ payload: { collectionId, sizePacksTolerance } }) => {
            return [mapSetOperation(collectionId, "pack_sizing_tolerance", sizePacksTolerance)];
        });
};
